/**
 * UK phone validation, mirroring `server/src/lib/phone.js`.
 *
 * The server is authoritative — this copy exists so the customer finds out
 * about a bad number while they are still looking at the field, rather than
 * after a submit. Keep the two rules identical: if this accepts something the
 * server rejects, the journey fails at the last step with nothing to show for
 * the click.
 *
 * The rule: eleven digits starting 0 (ten if they leave the leading zero off).
 * The customer-facing copy counts the eleven they actually type; the regex
 * below counts the ten that are left once the trunk zero is stripped.
 */

/** See the server file for why 4, 6 and 9 are excluded. */
const UK_NSN = /^[123578]\d{9}$/;

export function toUKNationalNumber(input) {
  let s = String(input ?? '').replace(/[\s().\-‐-―]/g, '');
  if (!/^\+?\d+$/.test(s)) return null;

  if (s.startsWith('+')) s = s.slice(1);
  else if (s.startsWith('00')) s = s.slice(2);

  if (s.startsWith('44') && s.length === 12) s = s.slice(2);
  else if (s.startsWith('0')) s = s.slice(1);

  return UK_NSN.test(s) ? s : null;
}

export function isValidUKPhone(input) {
  return toUKNationalNumber(input) !== null;
}

/** The hint shown under the field, and the phrasing the error echoes. */
export const UK_PHONE_HINT = 'UK numbers only — 11 digits, e.g. 07700 900123';

/**
 * One message per failure reason. A generic "invalid number" leaves the
 * customer guessing on a paid-traffic form; telling them they are two digits
 * short is something they can act on.
 */
export function ukPhoneError(input) {
  const raw = String(input ?? '').trim();
  if (!raw) return 'Please enter your phone number';

  if (/[^\d\s().+\-‐-―]/.test(raw)) return 'Please use digits only';

  const digits = raw.replace(/\D/g, '');
  if (!digits) return 'Please enter your phone number';

  // Count against the national number so the message is right whether or not
  // they typed the leading zero or a +44.
  const national = digits.replace(/^(44|0)/, '');
  if (national.length < 10) return 'That number is too short — UK numbers are 11 digits, e.g. 07700 900123';
  if (national.length > 10) return 'That number is too long — UK numbers are 11 digits, e.g. 07700 900123';

  if (!toUKNationalNumber(raw)) return 'That does not look like a UK number. We can only call UK numbers.';
  return null;
}
